// resources/js/src/components/community/stories/StoryPreviewModal.tsx
import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useEffect, useMemo, useState } from "react";
import { Icon } from "@iconify/react";
import { useStoryStore } from "../../../../../store/storyStore";

interface Props {
  isOpen: boolean;
  file: File | null;
  caption: string;
  setCaption: (s: string) => void;
  onClose: () => void;
  onPosted: () => void;
}

const StoryPreviewModal: React.FC<Props> = ({
  isOpen,
  file,
  caption,
  setCaption,
  onClose,
  onPosted,
}) => {
  const createStory = useStoryStore((s) => s.createStory);
  const isSaving = useStoryStore((s) => s.isSaving);
  const error = useStoryStore((s) => s.error);
  const [localError, setLocalError] = useState("");

  const previewUrl = useMemo(
    () => (file ? URL.createObjectURL(file) : ""),
    [file]
  );

  // Revoke URL on unmount / file change
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  if (!file) return null;

  const isVideo = file.type.startsWith("video");

  const handleShare = async () => {
    setLocalError("");
    const ok = await createStory({ file, caption: caption.trim() || undefined });
    if (ok) {
      setCaption("");
      onPosted();
      onClose();
    } else {
      setLocalError(error || "Failed to post story");
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm" />

        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="relative w-full max-w-md rounded-xl bg-deepSlate overflow-hidden">
            <button
              onClick={onClose}
              disabled={isSaving}
              className="absolute top-3 right-3 z-10 w-9 h-9 flex items-center justify-center rounded-full bg-black/60 text-white hover:bg-black transition disabled:opacity-50"
              aria-label="Close"
            >
              ✕
            </button>

            {isVideo ? (
              <video
                src={previewUrl}
                controls
                autoPlay
                className="w-full h-[500px] object-contain"
              />
            ) : (
              <img
                src={previewUrl}
                alt="Story Preview"
                className="w-full h-[500px] object-contain p-3"
              />
            )}

            {/* Caption */}
            <div className="px-4 pt-2">
              <input
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                placeholder="Add a caption..."
                disabled={isSaving}
                className="w-full rounded-lg px-3 py-2 text-sm bg-charcoalGray/20 dark:bg-dark_grey focus:outline-none"
                maxLength={200}
              />
            </div>

            {localError && (
              <p className="px-4 pt-1 text-xs text-red-400">{localError}</p>
            )}

            <div className="flex justify-end p-4 bg-black">
              <button
                onClick={handleShare}
                disabled={isSaving}
                className="cursor-pointer text-white dark:text-midnight_text dark:bg-primary bg-tealGreen px-4 py-1 rounded-lg disabled:opacity-50 flex items-center gap-2"
              >
                {isSaving ? (
                  <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current/30 border-t-current" />
                ) : (
                  <Icon icon="mdi:send" className="w-4 h-4" />
                )}
                Share
              </button>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>
    </Transition>
  );
};

export default StoryPreviewModal;